/**
 * Platform-wide billing currencies for ReHub.
 *
 * GBP, EUR and USD are first-class. Amounts are always integer minor units
 * (pence / cents). Never convert with a live FX rate — each product keeps
 * its own fixed regional prices.
 *
 * ISO codes only (`GBP` | `EUR` | `USD`). Never store £ € $ as identifiers.
 */

export const REHUB_CURRENCIES = ['GBP', 'EUR', 'USD'] as const;
export type RehubCurrency = (typeof REHUB_CURRENCIES)[number];

/** @deprecated Use RehubCurrency. Kept so existing imports keep compiling. */
export type BillingCurrency = RehubCurrency;

export const CURRENCY_SYMBOL: Record<RehubCurrency, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

export const CURRENCY_LABEL: Record<RehubCurrency, string> = {
  GBP: 'UK £',
  EUR: 'EU €',
  USD: 'US $',
};

export const CURRENCY_SHORT_LABEL: Record<RehubCurrency, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

export const CURRENCY_ACCESSIBLE_LABEL: Record<RehubCurrency, string> = {
  GBP: 'UK pounds',
  EUR: 'Euros',
  USD: 'US dollars',
};

/**
 * Official eurozone + euro-using microstates. Do not treat every European
 * country as EUR (CH, SE, DK, NO, PL, CZ, HU, RO stay out).
 * Bulgaria joined the euro on 1 January 2026.
 */
export const EUROZONE_COUNTRIES = [
  'AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK', 'BG',
  'AD', 'MC', 'SM', 'VA', 'ME',
] as const;

const EUROZONE_SET = new Set<string>(EUROZONE_COUNTRIES);

const NON_EURO_EUROPE_TIMEZONES = new Set([
  'Europe/London',
  'Europe/Belfast',
  'Europe/Guernsey',
  'Europe/Isle_of_Man',
  'Europe/Jersey',
  'Europe/Zurich',
  'Europe/Vaduz',
  'Europe/Stockholm',
  'Europe/Copenhagen',
  'Europe/Oslo',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/Belgrade',
  'Europe/Sarajevo',
  'Europe/Skopje',
  'Europe/Tirane',
  'Europe/Kyiv',
  'Europe/Kiev',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Atlantic/Reykjavik',
]);

const EURO_LOCALES = /^(de|fr|es|it|nl|pt|fi|el|et|lv|lt|sk|sl|hr|mt|ga|eu|ca)([-_]|$)/i;

export const BILLING_CURRENCY_STORAGE_KEY = 'rehub_billing_currency';

/**
 * HDRPhoto EUR checkout is approved and live.
 * Wider ReHub EUR checkout stays off until dedicated Stripe Price IDs exist
 * (after the proposed EUR price table is approved).
 */
export const EUR_LIVE_CHECKOUT_APPS = ['hdrphoto'] as const;

export function isEurozoneCountry(country: string | null | undefined): boolean {
  return EUROZONE_SET.has(String(country || '').toUpperCase());
}

export function parseRehubCurrency(raw: string | null | undefined): RehubCurrency | null {
  const value = String(raw || '').trim().toUpperCase();
  if (value === 'GBP' || value === 'EUR' || value === 'USD') return value;
  return null;
}

export function normalizeRehubCurrency(
  raw: string | null | undefined,
  fallback: RehubCurrency = 'GBP',
): RehubCurrency {
  return parseRehubCurrency(raw) ?? fallback;
}

export function isRehubCurrency(raw: string | null | undefined): raw is RehubCurrency {
  return parseRehubCurrency(raw) !== null;
}

export function stripeCurrencyCode(currency: RehubCurrency): string {
  return currency.toLowerCase();
}

export function minorToMajor(amountMinor: number): number {
  return amountMinor / 100;
}

export interface FormatMoneyOptions {
  /** Force a fixed number of fraction digits. */
  decimals?: number;
  /** Whole amounts render as £59 rather than £59.00 unless decimals is set. */
  stripZeroDecimals?: boolean;
}

/**
 * Central money formatter. Examples: £59, €59, $75, £8.50, €0.44.
 * Never concatenates a symbol onto an already-formatted string.
 */
export function formatMoney(amountMajor: number, currency: RehubCurrency, opts?: FormatMoneyOptions): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? '';
  if (!Number.isFinite(amountMajor)) return `${symbol}0`;
  const decimals = opts?.decimals;
  if (decimals != null) {
    return `${symbol}${amountMajor.toFixed(decimals)}`;
  }
  const strip = opts?.stripZeroDecimals !== false;
  if (strip && Number.isInteger(amountMajor)) return `${symbol}${amountMajor}`;
  const places = Math.abs(amountMajor) < 10 ? 2 : 2;
  const formatted = amountMajor.toFixed(places);
  if (strip && formatted.endsWith('.00')) return `${symbol}${Math.round(amountMajor)}`;
  return `${symbol}${formatted}`;
}

export function formatMoneyFromMinor(
  amountMinor: number,
  currency: RehubCurrency,
  opts?: FormatMoneyOptions,
): string {
  return formatMoney(minorToMajor(amountMinor), currency, opts);
}

/** Checkout-facing totals always show two decimals (£8.50, €9.90, $11.50). */
export function formatCheckoutTotal(amountMinor: number, currency: RehubCurrency): string {
  return formatMoneyFromMinor(amountMinor, currency, { decimals: 2 });
}

export function formatPerUnitFromMinor(
  amountMinor: number,
  units: number,
  currency: RehubCurrency,
): string {
  if (!units || units <= 0) return formatMoney(0, currency, { decimals: 2 });
  return formatMoney(amountMinor / units / 100, currency, { decimals: 2 });
}

export function currencySymbol(currency: RehubCurrency): string {
  return CURRENCY_SYMBOL[currency];
}

/** True when a dedicated Stripe Price ID is required (EUR never uses price_data). */
export function requiresDedicatedStripePrice(currency: RehubCurrency): boolean {
  return currency === 'EUR';
}

export function canCheckoutInCurrency(opts: {
  currency: RehubCurrency;
  stripePriceId?: string | null;
  app?: string | null;
}): boolean {
  if (opts.currency === 'GBP' || opts.currency === 'USD') return true;
  if (opts.app && (EUR_LIVE_CHECKOUT_APPS as readonly string[]).includes(opts.app)) return true;
  return Boolean(opts.stripePriceId);
}

function timezoneLooksEuro(tz: string): boolean {
  if (!tz) return false;
  if (!tz.startsWith('Europe/')) return false;
  return !NON_EURO_EUROPE_TIMEZONES.has(tz);
}

function localeLooksEuro(locale: string): boolean {
  const lower = locale.toLowerCase();
  if (lower.includes('-gb') || lower.startsWith('en-gb')) return false;
  if (lower.includes('-us') || lower.startsWith('en-us')) return false;
  if (lower.includes('-ch') || lower.includes('-se') || lower.includes('-dk') || lower.includes('-no')) {
    return false;
  }
  if (lower.includes('-pl') || lower.includes('-cz') || lower.includes('-hu') || lower.includes('-ro')) {
    return false;
  }
  return EURO_LOCALES.test(lower) || /[-_](at|be|cy|de|ee|es|fi|fr|gr|hr|ie|it|lt|lu|lv|mt|nl|pt|si|sk|bg)\b/i.test(lower);
}

/**
 * Display-currency detection. Subscription billing currency always wins so an
 * existing USD subscriber is never silently shown (or charged) EUR/GBP.
 */
export function detectBillingCurrency(opts?: {
  stored?: string | null;
  accountCountry?: string | null;
  subscriptionCurrency?: string | null;
  locale?: string | null;
  timeZone?: string | null;
}): RehubCurrency {
  const locked = parseRehubCurrency(opts?.subscriptionCurrency);
  if (opts?.subscriptionCurrency && locked) return locked;

  const stored = parseRehubCurrency(opts?.stored);
  if (stored) return stored;

  const country = (opts?.accountCountry || '').toUpperCase();
  if (country === 'GB' || country === 'UK') return 'GBP';
  if (country === 'US') return 'USD';
  if (isEurozoneCountry(country)) return 'EUR';
  if (country) return 'USD';

  const locale = opts?.locale || (typeof navigator !== 'undefined' ? navigator.language : '');
  const tz =
    opts?.timeZone ||
    (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '');

  if (tz === 'Europe/London' || (locale || '').toLowerCase().startsWith('en-gb')) return 'GBP';
  if ((locale || '').toLowerCase().startsWith('en-us') || (tz || '').startsWith('America/')) return 'USD';
  if (timezoneLooksEuro(tz) || localeLooksEuro(locale || '')) return 'EUR';

  return 'GBP';
}

export function persistBillingCurrency(currency: RehubCurrency): void {
  try {
    localStorage.setItem(BILLING_CURRENCY_STORAGE_KEY, currency);
    sessionStorage.setItem(BILLING_CURRENCY_STORAGE_KEY, currency);
  } catch {
    /* ignore */
  }
}

export function readPersistedBillingCurrency(): RehubCurrency | null {
  try {
    return parseRehubCurrency(
      localStorage.getItem(BILLING_CURRENCY_STORAGE_KEY)
        || sessionStorage.getItem(BILLING_CURRENCY_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

export function priceFromBook<T extends Record<RehubCurrency, number>>(
  book: T,
  currency: RehubCurrency,
): number {
  return book[currency];
}
